import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
  ChangeEvent,
  MouseEvent,
} from "react";
import toast from "react-hot-toast";
import type { File } from "src/components/file-dropzone";
import {
  GenericPath,
  useUploadProfessionalGroupImage,
  useUploadProfessionalGroupRoleImage,
  useV3Create,
  useV3DeleteById,
  useV3DocumentDownload,
  useV3DocumentDownloadBlob,
  useV3GenericGetData,
  useV3GenericGetDataWithPagination,
  useV3Update,
  useV3UploadMultipleFiles,
} from "src/utils/snug/estateAppApi";
import { downloadFileFromS3 } from "src/utils/snug/snug";
import { useMounted } from "src/hooks/use-mounted";
import {
  AuthorizationRoleTypes,
  PeopleRole,
  DocumentV2,
  ProfessionalGroup,
  ProfessionalGroupRole,
  UserDataObj,
  Household,
} from "src/types/snugtotal";
import { slackHook } from "src/utils/snug/slackHooks";
import { User } from "@auth0/auth0-spa-js";
import { useAuth } from "src/hooks/use-auth";
import { SplashScreen } from "src/components/splash-screen";
import { paths } from "src/paths";
import { useRouter } from "src/hooks/use-router";
import useWhiteLabelConfig from "./config-context";
import { gtm } from "src/libs/gtm";

type SortDir = "asc" | "desc";

export interface PatchableProGroupRoleObject {
  professional_email?: string;
  professional_phone?: string;
  professional_address?: string;
  professional_image?: string;
  professional_source_image?: string;
  default_will_price?: number;
  default_trust_price?: number;
  professional_website?: string;
  professional_bio?: string;
  docx_on?: boolean;
  notifications_client_added?: boolean;
  notifications_client_purchase?: boolean;
  notifications_client_signup?: boolean;
}

interface ClientHouseholdsSearchState {
  filter?: string;
  page: number;
  rowsPerPage: number;
  sortBy?: string;
  sortDir?: SortDir;
}

const useClientRoleSearch = () => {
  const [searchAndPaginationState, setSearchAndPaginationState] =
    useState<ClientHouseholdsSearchState>({
      filter: undefined,
      page: 0,
      rowsPerPage: 25,
      sortBy: "created_at",
      sortDir: "desc",
    });

  const handleFiltersChange = useCallback((filter: string): void => {
    setSearchAndPaginationState((prevState) => ({
      ...prevState,
      filter,
    }));
  }, []);

  const handleSortChange = useCallback(
    (sortBy: string, sortDir: SortDir): void => {
      setSearchAndPaginationState((prevState) => ({
        ...prevState,
        sortBy,
        sortDir,
      }));
    },
    []
  );

  const handlePageChange = useCallback(
    (event: MouseEvent<HTMLButtonElement> | null, page: number): void => {
      setSearchAndPaginationState((prevState) => ({
        ...prevState,
        page,
      }));
    },
    []
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSearchAndPaginationState((prevState) => ({
        ...prevState,
        rowsPerPage: parseInt(event.target.value, 10),
      }));
    },
    []
  );

  return {
    handleFiltersChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    searchAndPaginationState,
  };
};


type clientHouseholdsStoreAction =
  | { type: "SET_CLIENT_HOUSEHOLDS"; payload: Household[] }
  | { type: "SET_CLIENT_HOUSEHOLDS_SEARCH_COUNT"; payload: number }
  | { type: "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT"; payload: number }
  | { type: "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT_LOADING"; payload: boolean }
  | {
    type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS";
    payload: Household;
  }
  | { type: "DELETE_CLIENT_HOUSEHOLDS"; payload: string }
  | { type: "SET_CLIENT_HOUSEHOLDS_LOADING"; payload: boolean }
  | { type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW"; payload: boolean }
  | { type: "SET_CLIENT_HOUSEHOLDS_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_ASSUMED_ACCOUNT_ROLE"; payload: AuthorizationRoleTypes | null }
  | { type: "SET_TARGET_USER_DATAS"; payload: UserDataObj[] }
  | { type: "SET_TARGET_USER_DATA_SAVING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_TARGET_USER_DATA"; payload: UserDataObj }
  | { type: "SET_USERDATA"; payload: UserDataObj }
  | { type: "SET_USERDATA_INITIAL_LOADING"; payload: boolean }
  | { type: "SET_USERDATA_SAVING"; payload: boolean }
  | { type: "SET_PROFESSIONAL_GROUP"; payload: ProfessionalGroup | null }
  | { type: "SET_WHITELABEL_CONFIG_SAVING"; payload: boolean };

// Define reducer function for useclientHouseholdsStore
const clientHouseholdsStoreReducer = (
  state: UserDataContextStoreState,
  action: clientHouseholdsStoreAction
): UserDataContextStoreState => {
  switch (action.type) {
    case "SET_CLIENT_HOUSEHOLDS":
      return { ...state, clientHouseholds: action.payload };
    // SET_CLIENT_HOUSEHOLDS_SEARCH_COUNT
    case "SET_CLIENT_HOUSEHOLDS_SEARCH_COUNT":
      return { ...state, clientHouseholdsSearchCount: action.payload };
    case "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT":
      return { ...state, clientHouseholdsTotalCount: action.payload };
    case "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT_LOADING":
      return { ...state, clientHouseholdsTotalCountLoading: action.payload };
    case "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS":
      let clientHouseholds = state.clientHouseholds;
      const index = clientHouseholds.findIndex((pr) => pr.household_role.id === action.payload.household_role.id);
      if (index > -1) {
        clientHouseholds[index] = action.payload;
      } else {
        clientHouseholds = [action.payload, ...clientHouseholds];
      }
      return { ...state, clientHouseholds };
    case "DELETE_CLIENT_HOUSEHOLDS":
      return {
        ...state,
        clientHouseholds: state.clientHouseholds.filter((pr) => pr.household_role.id !== action.payload),
      };
    case "SET_CLIENT_HOUSEHOLDS_LOADING":
      return { ...state, clientHouseholdsLoading: action.payload };
    case "SET_CLIENT_HOUSEHOLDS_SAVING_NEW":
      return { ...state, clientHouseholdsSavingNew: action.payload };
    case "SET_CLIENT_HOUSEHOLDS_FILES_TO_UPLOAD":
      return { ...state, clientHouseholdsFilestoUpload: action.payload };
    case "SET_USERDATA":
      return { ...state, userData: action.payload };
    case "SET_USERDATA_INITIAL_LOADING":
      return { ...state, userDataInitialLoading: action.payload };
    case "SET_USERDATA_SAVING":
      return { ...state, userDataSaving: action.payload };
    case "SET_PROFESSIONAL_GROUP":
      return { ...state, professionalGroup: action.payload };
    case "SET_WHITELABEL_CONFIG_SAVING":
      return { ...state, whitelabelConfigSaving: action.payload };
    default:
      return state;
  }
};

export interface UserDataContexType {
  userData: UserDataObj | null;
  userDataInitialLoading: boolean;
  userDataSaving: boolean;
  clientHouseholds: Household[];
  clientHouseholdsSearchCount: number;
  clientHouseholdsTotalCount: number;
  clientHouseholdsTotalCountLoading: boolean;
  clientHouseholdsLoading: boolean;
  clientHouseholdsSavingNew: boolean;
  clientHouseholdsFilestoUpload: File[];
  professionalGroup: ProfessionalGroup | null;
  professionalGroupSaving: boolean;
  whitelabelConfigSaving: boolean;
  searchAndPaginationState: ClientHouseholdsSearchState;
  isFirmAdmin: boolean;
  isPaidTier: boolean;
  // handleFiltersChange: (filters: Filters) => void;
  handleFiltersChange: (filter: string) => void;
  handleSortChange?: (sortBy: string, sortDir: SortDir) => void;
  handlePageChange: (
    event: MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  handleRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleUpdateProfessionalGroup: (data: ProfessionalGroup) => Promise<void>;
  handleUpdateProfessionalGroupImage: (
    file: File,
    sourceFile: File,
    professionalGroup: ProfessionalGroup | null
  ) => Promise<void>;
  handleDeleteProGroupImage: () => Promise<void>;
  handleArchiveClientRole: (client_role_id: string | null) => Promise<void>;
  handleDownloadClientEstateDocument: (
    ud_id: string,
    estateDocumentId: string,
    document: DocumentV2 | null
  ) => Promise<void>;
  handleDownloadAllClientEstateDocuments: (
    ud_id: string,
    estateDocumentId: string
  ) => Promise<void>;
  handleclientHouseholdsFileRemove: (file: File) => void;
  handleclientHouseholdsFilesDrop: (newFiles: File[]) => void;
  handleclientHouseholdsFilesRemoveAll: () => void;
  handleUploadClientHouseholdsFiles: (entityId: string | null) => void;
  handleAddOrEditTargetUserData: (
    target_ud_id: string,
    userData: UserDataObj
  ) => Promise<UserDataObj | null>;
  handleUpdateWhiteLabelConfig: (data: {
    email_sender_name?: string;
    product_name?: string;
    disclaimer?: string;
    domain_provider?: string;
    domain?: string;
    dashboard_domain?: string;
    email_sender?: string;
  }) => Promise<void>;

  handleDeleteProfessionalImage: () => Promise<void>;
  handleUpdateProfessionalImage: (
    file: File,
    sourceFile: File
  ) => Promise<void>;
  handleAddOrEditOwnerUserData: (
    userData: UserDataObj,
    proGroupRoleUpdate?: PatchableProGroupRoleObject
  ) => Promise<UserDataObj | null>;
  handleInvitePro: (
    professional_group_id: string,
    name: string,
    email: string,
    role: "PRO_GROUP_ADMIN" | "PRO_GROUP_AGENT"
  ) => void;
  handleSendInvitation: (role_id: string) => void;
  handleInviteClient: (
    userDataUdId: string,
    customMessage: string | null,
    clientNotification?: boolean
  ) => void;
  handleUpgradeToAdmin: (role_id: string) => void;
  handleDowngradeToAgent: (role_id: string) => void;
  handleDeleteProGroupRole: (
    role_id: string,
    new_user_data_ud_id: string
  ) => void;
  handleDeletePeopleRole: (
    ud_id: string,
    people_role_id: string
  ) => Promise<void>;
  handleAdminUpdateClient: (
    client_role_id: string,
    client_role_updates: Partial<PeopleRole>
  ) => Promise<Household>;
  handleTransferClient: (
    client_role_id: string,
    transfer_to_professional_role_id: string,
    transfer_from_professional_role_id: string
  ) => void;
  handleRequestAccessFromLead: (client_role_id: string) => void;
  handleEditClientEmail: (
    client_role_id: string,
    email: string,
    send_invite: boolean
  ) => Promise<void>;
  handleRefreshProGroup: () => Promise<ProfessionalGroup | null>;
  handlePatchSelfProfessionalGroupRole: (
    data: PatchableProGroupRoleObject
  ) => Promise<ProfessionalGroupRole | void>;
  handleAddClient: (data: any) => Promise<Household>;
  updateProfessionalGroupState: (professionalGroup: ProfessionalGroup) => void;
  handlePatchProfessionalGroup: (professionalGroupId: string, data: Partial<ProfessionalGroup>) => Promise<void>;
  updateHeadOfHouseholdUserData: (
    houseHoldId: string,
    hoh_ud_id: string,
    userData: UserDataObj
  ) => Promise<void>;
  handleRefreshHousehold: (householdId: string) => Promise<void>;
}

interface UserDataContextStoreState {
  clientHouseholds: Household[];
  clientHouseholdsSearchCount: number;
  clientHouseholdsTotalCount: number;
  clientHouseholdsTotalCountLoading: boolean;
  clientHouseholdsLoading: boolean;
  clientHouseholdsSavingNew: boolean;
  clientHouseholdsFilestoUpload: File[];
  professionalGroup: ProfessionalGroup | null;
  userData: UserDataObj | null;
  userDataInitialLoading: boolean;
  userDataSaving: boolean;
  whitelabelConfigSaving: boolean;
}

export const UserDataContext = createContext<UserDataContexType>(
  {} as UserDataContexType
);

interface UserDataProviderProps {
  children: ReactNode;
}

// Possible refactore would be to handle api selection here
export const UserDataProvider: FC<UserDataProviderProps> = (props) => {
  const { children } = props;
  const isMounted = useMounted();
  const { user, signOut } = useAuth();
  const router = useRouter();
  const { email_sender, dashboard_domain } = useWhiteLabelConfig();
  const {
    handleFiltersChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    searchAndPaginationState,
  } = useClientRoleSearch();

  const [state, dispatch] = useReducer(clientHouseholdsStoreReducer, {
    clientHouseholds: [],
    clientHouseholdsSearchCount: 0,
    clientHouseholdsTotalCount: 0,
    clientHouseholdsTotalCountLoading: false,
    clientHouseholdsLoading: true,
    clientHouseholdsSavingNew: false,
    clientHouseholdsFilestoUpload: [],
    professionalGroup: null,
    userData: null,
    userDataInitialLoading: true,
    userDataSaving: false,
    whitelabelConfigSaving: false,
  });

  const deleteRoleClientById = useV3DeleteById(
    GenericPath.GENERIC_SUBCATEGORY_ITEM_RETRIEVE_UPDATE_DELETE_V3
  );

  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );

  const downloadAllDocuments = useV3DocumentDownloadBlob(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_ALL_V3
  );

  const addClient = useV3Create<Household>(
    GenericPath.GENERIC_SUBCATEGORY_ITEM_LIST_CREATE_V3
  );
  const analyzeDocument =
    useV3UploadMultipleFiles<PeopleRole>(
      GenericPath.GENERIC_SUBCATEGORY_ITEM_RETRIEVE_UPDATE_DELETE_V3
    );

  const adminPathClientRole = useV3Update<Household>(
    GenericPath.GENERIC_SUBCATEGORY_ITEM_RETRIEVE_UPDATE_DELETE_V3
  );

  const genericGetHouseholds =
    useV3GenericGetDataWithPagination<Household>(
      GenericPath.GENERIC_SUBCATEGORY_ITEM_LIST_CREATE_V3
    );

  const genericGetUserData = useV3GenericGetData<UserDataObj>(
    GenericPath.USER_DATA_V3
  );
  const createUserData = useV3Create<UserDataObj>(GenericPath.USER_DATA_V3);
  const updateUserData = useV3Update<UserDataObj>(GenericPath.USER_DATA_V3);
  const updateTargetUserData = useV3Update<UserDataObj>(GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3);
  const removeProfessionalImage = useV3DeleteById(GenericPath.PRO_IMAGE);
  const updateProfessionalImage =
    useUploadProfessionalGroupRoleImage<ProfessionalGroupRole>(
      GenericPath.PRO_IMAGE
    );
  const invitePro = useV3Create<ProfessionalGroup>(
    GenericPath.PRO_GROUP_INVITE
  );
  const sendProRoleInvite = useV3Create(
    GenericPath.PRO_GROUP_ROLE_SEND_INVITE
  );
  const sendClientInvite = useV3Create(GenericPath.CLIENT_INVITE);
  const transferClient = useV3Update<PeopleRole>(
    GenericPath.CLIENT_TRANSFER
  );
  const upgradeToAdmin = useV3Create<ProfessionalGroup>(
    GenericPath.PRO_GROUP_ROLE_UPGRADE
  );
  const downgradeToAgent = useV3Create<ProfessionalGroup>(
    GenericPath.PRO_GROUP_ROLE_DOWNGRADE
  );
  const deleteProGroupRole = useV3DeleteById(
    GenericPath.GENERIC_SUBCATEGORY_ITEM_RETRIEVE_UPDATE_DELETE_V3
  );
  const deletePeopleRole = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const requestAccessFromLead =
    useV3Update<PeopleRole>(
      GenericPath.LEAD_REQUEST_ACCESS
    );
  const editClientEmail = useV3Update<PeopleRole>(
    GenericPath.CLIENT_EDIT_EMAIL
  );
  const getClientCount = useV3GenericGetData<{ count: number }>(
    GenericPath.CLIENT_COUNT
  );
  const patchSelfProfessionalGroupRole = useV3Update<ProfessionalGroupRole>(
    GenericPath.GENERIC_SUBCATEGORY_ITEM_RETRIEVE_UPDATE_DELETE_V3
  );

  const handleUpdateWhiteLabelConfigOnProGroup = useV3Update<ProfessionalGroup>(
    GenericPath.WHITE_LABEL_CONFIG_UPDATE
  );

  const genericGetHousehold = useV3GenericGetData<Household>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  const handlePatchSelfProfessionalGroupRole = useCallback(
    async (
      data: PatchableProGroupRoleObject
    ): Promise<ProfessionalGroupRole | void> => {
      try {
        if (!state.userData?.ud_id) {
          console.error("No user data id provided");
          return;
        }

        const params = {
          ud_id: state.userData.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id || "",
          subcategory: "professional-group-role",
          subcategory_id:
            state.userData.professional_group_role_user_data?.id || "",
        };

        // strip fields from data that are null, undefined, or empty string
        Object.keys(data).forEach(
          (key) =>
            (data[key as keyof PatchableProGroupRoleObject] == null ||
              data[key as keyof PatchableProGroupRoleObject] === "") &&
            delete data[key as keyof PatchableProGroupRoleObject]
        );
        if (Object.keys(data).length === 0) {
          return;
        }
        const expandQuery = { expand: "professional" };
        return await patchSelfProfessionalGroupRole(params, data, true, expandQuery);
      } catch (err) {
        console.error(err);
      }
    },
    [
      patchSelfProfessionalGroupRole,
      state.professionalGroup?.id,
      state.userData?.professional_group_role_user_data?.id,
      state.userData?.ud_id,
    ]
  );

  const handleAddOrEditOwnerUserData = useCallback(
    async (
      userData: UserDataObj,
      proGroupRoleUpdate?: PatchableProGroupRoleObject
    ): Promise<UserDataObj | null> => {
      try {
        dispatch({ type: "SET_USERDATA_SAVING", payload: true });
        const params = { ud_id: "", entity: "user_data" };
        const expandQuery = { expand: "professional_group_role.professional" };
        if (userData.ud_id) {
          let proGroupRole = { ...userData.professional_group_role_user_data };
          if (proGroupRoleUpdate) {
            const proGroupRoleRes = await handlePatchSelfProfessionalGroupRole(
              proGroupRoleUpdate
            );
            if (proGroupRoleRes) {
              proGroupRole = proGroupRoleRes;
            }
          }

          const res = await updateUserData(params, userData, true, expandQuery);
          dispatch({
            type: "SET_USERDATA",
            payload: {
              ...res,
              professional_group_role_user_data: proGroupRole,
            },
          });
          return res;
        } else {
          const res = await createUserData(params, userData, expandQuery);
          dispatch({ type: "SET_USERDATA", payload: res });
          return res;
        }
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        dispatch({ type: "SET_USERDATA_SAVING", payload: false });
      }
    },
    [createUserData, updateUserData, handlePatchSelfProfessionalGroupRole]
  );

  const updateProfessionalGroupState = useCallback(
    (professionalGroup: ProfessionalGroup) => {
      dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: professionalGroup });
    },
    [dispatch]
  );

  const handleRequestAccessFromLead = useCallback(
    async (client_role_id: string) => {
      if (!state.userData?.ud_id) {
        console.error("No user data id provided");
        return Promise.reject("No user data id provided");
      }

      try {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: true });
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id || "",
          subcategory: "pro-people-roles",
          subcategory_id: client_role_id,
        };
        const res = await requestAccessFromLead(params, {}, true);
        // get household from existing clientHouseholds and update the household_role with the new household_role
        const updatedHouseholds = state.clientHouseholds.find((household) => household.household_role?.id === client_role_id);
        if (updatedHouseholds) {
          updatedHouseholds.household_role = res;
        }
        if (updatedHouseholds) {
          dispatch({ type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS", payload: updatedHouseholds });
        }
        return res;
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    [requestAccessFromLead, state.userData?.ud_id, state.professionalGroup?.id, state.clientHouseholds]
  );

  const handleDeleteProfessionalImage = useCallback(async () => {
    if (!state.userData) {
      console.error("No user data provided");
      return Promise.reject("No user data provided");
    }

    try {
      dispatch({ type: "SET_USERDATA_SAVING", payload: true });
      const params = {
        entity_id: state.professionalGroup?.id,
        ud_id: state.userData?.ud_id,
        subcategory_id:
          state.userData.professional_group_role_user_data?.id || "",
      };
      await removeProfessionalImage(params);
      // response is empty object. remove professional image from field in state object
      const newUserData = {
        ...(state.userData as UserDataObj),
        professional_group_role_user_data: {
          ...state.userData.professional_group_role_user_data,
          professional_image: null,
        },
      };
      dispatch({ type: "SET_USERDATA", payload: newUserData });
      toast.success("Your image has been deleted!");
    } catch (error) {
      console.error(error);
      toast.error(
        "There's an error deleting your advisor image! Please try again."
      );
    } finally {
      dispatch({ type: "SET_USERDATA_SAVING", payload: false });
    }
  }, [removeProfessionalImage, state.userData, state.professionalGroup?.id]);


  const updateHeadOfHouseholdUserData = useCallback(
    async (houseHoldId: string, hoh_ud_id: string, userData: UserDataObj) => {
      if (!hoh_ud_id || !houseHoldId) {
        console.error("No user data id provided");
        return Promise.reject("No user data id provided");
      }

      try {
        const params = { ud_id: state.userData?.ud_id || "", entity: "role-target-user-data", entity_id: hoh_ud_id };
        const expandQuery = { expand: "estate_documents,household" };
        const res = await updateTargetUserData(
          params,
          {...userData, ud_id: undefined},
          true,
          expandQuery
        );
        // update the head of household by replacing the head of household in the state array with the response
        const updatedHouseholds = state.clientHouseholds.map((household) => {
          if (household.household_role?.id === houseHoldId) {
            return { ...household, head_of_household: res };
          }
          return household;
        });
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS", payload: updatedHouseholds });

      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    [updateTargetUserData, state.userData, state.clientHouseholds]);


  const handleUpdateProfessionalImage = useCallback(
    async (file: File, sourceFile: File) => {
      try {
        const expandQuery = { expand: "professional" };
        dispatch({ type: "SET_USERDATA_SAVING", payload: true });
        const res = await updateProfessionalImage(
          state?.userData?.ud_id || "",
          state?.professionalGroup?.id || "",
          state?.userData?.professional_group_role_user_data?.id || "",
          file,
          sourceFile,
          expandQuery
        );
        dispatch({
          type: "SET_USERDATA",
          payload: {
            ...(state?.userData as UserDataObj),
            professional_group_role_user_data: res,
          },
        });
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        dispatch({ type: "SET_USERDATA_SAVING", payload: false });
      }
    },
    [updateProfessionalImage, state?.professionalGroup?.id, state?.userData]
  );

  const handleAddOrEditTargetUserData = useCallback(
    async (
      target_ud_id: string,
      userData: UserDataObj
    ): Promise<UserDataObj | null> => {
      if (!target_ud_id) {
        console.error("No user data id provided");
        return Promise.reject("No user data id provided");
      }

      try {
        dispatch({ type: "SET_TARGET_USER_DATA_SAVING", payload: true });
        const params = { ud_id: target_ud_id, entity: "role-target-user-data" };
        if (userData.ud_id) {
          const res = await updateUserData(
            { ...params, entity_id: userData.ud_id },
            userData
          );
          dispatch({ type: "ADD_OR_UPDATE_TARGET_USER_DATA", payload: res });
          return res;
        } else {
          const res = await createUserData(params, userData);
          dispatch({ type: "ADD_OR_UPDATE_TARGET_USER_DATA", payload: res });
          return res;
        }
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        dispatch({ type: "SET_TARGET_USER_DATA_SAVING", payload: false });
      }
    },
    [createUserData, updateUserData]
  );

  const handleclientHouseholdsFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_CLIENT_HOUSEHOLDS_FILES_TO_UPLOAD",
        payload: [...state.clientHouseholdsFilestoUpload, ...newFiles],
      });
    },
    [state.clientHouseholdsFilestoUpload]
  );

  const handleclientHouseholdsFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.clientHouseholdsFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_CLIENT_HOUSEHOLDS_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.clientHouseholdsFilestoUpload]
  );

  const handleclientHouseholdsFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_CLIENT_HOUSEHOLDS_FILES_TO_UPLOAD", payload: [] });
  }, []);

  const handleUploadClientHouseholdsFiles = useCallback(
    async (peopleRoleId: string | null) => {
      if (!state.userData?.ud_id || !peopleRoleId) {
        console.error(`Missing required parameters ud_id or peopleRoleId`);
        return Promise.reject("Missing required parameters");
      }
      dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: true });

      const files = state.clientHouseholdsFilestoUpload;

      if (files.length === 0) {
        toast.error("No files to upload");
        return;
      }

      try {
        // Call the API to upload all files in one request
        const res = await analyzeDocument(
          {
            ud_id: state.userData.ud_id,
            entity: "pro-group",
            entity_id: state.professionalGroup?.id || "",
            subcategory: "client-analyze",
            subcategory_id: peopleRoleId,
          },
          files
        );

        if (res) {
          // Update state with the response
          const updatedHouseholds = state.clientHouseholds.find((household) => household.household_role?.id === peopleRoleId);
          if (updatedHouseholds) {
            updatedHouseholds.household_role = res;
          }
          if (updatedHouseholds) {
            dispatch({ type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS", payload: updatedHouseholds });
          }

          // Clear the files after successful upload
          dispatch({
            type: "SET_CLIENT_HOUSEHOLDS_FILES_TO_UPLOAD",
            payload: [],
          });

          const successMessage = `${files.length} files uploaded successfully`;
          toast.success(successMessage);
        } else {
          const errorMessage = `${files.length} files failed to upload`;
          toast.error(errorMessage);
          slackHook(
            `Document Error: Failed to upload documents for entity_id: ${peopleRoleId}`
          );
        }
      } catch (error) {
        const errorMessage = `Failed to upload all documents`;
        toast.error(errorMessage);
        console.error(errorMessage);
        slackHook(
          `Document Error: Failed to upload documents for entity_id: ${peopleRoleId} with message "${error}"`
        );
      } finally {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: false });
        // Ensure files are cleared after the upload process
        handleclientHouseholdsFilesRemoveAll();
      }
    },
    [
      analyzeDocument,
      state.clientHouseholdsFilestoUpload,
      state.userData?.ud_id,
      state.professionalGroup?.id,
      handleclientHouseholdsFilesRemoveAll,
      state.clientHouseholds,
    ]
  );

  const handleDownloadClientEstateDocument = useCallback(
    async (
      ud_id: string,
      estateDocumentId: string,
      document: DocumentV2 | null
    ) => {
      if (
        !document?.document_id ||
        !estateDocumentId ||
        !state.userData?.ud_id
      ) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document.document_id,
        entity: "estate-documents",
        entity_id: estateDocumentId,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in tax objects' documents array
            downloadFileFromS3(
              res,
              document.document_name || "Snug_Download.error"
            );
          } else {
            slackHook(
              `Document Error: Failed get document url from location with message "No response"`
            );
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          slackHook(
            `Document Error: Failed get document url from location with message "${err}"`
          );
        });
    },
    [downloadDocument, state.userData?.ud_id]
  );

  const handleDownloadAllClientEstateDocuments = useCallback(
    async (
      ud_id: string,
      estateDocumentId: string
    ) => {
      if (
        !estateDocumentId ||
        !state.userData?.ud_id
      ) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading all files...");

      const params: any = {
        ud_id: ud_id,
        entity: "estate-documents",
        entity_id: estateDocumentId,
      };

      try {
        const { blob, filename } = await downloadAllDocuments(params);

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link element and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Download completed successfully!");
      } catch (err) {
        toast.error("Failed to download all files");
        slackHook(
          `Document Error: Failed get document url from location with message "${err}"`
        );
      }
    },
    [downloadAllDocuments, state.userData?.ud_id]
  );

  const handleArchiveClientRole = useCallback(
    async (
      client_role_id: string | null
      // auth_role_id: string | null,
      // second_degree_auth_role_ids: (string | null)[] | null
    ): Promise<void> => {
      if (!client_role_id || !state.userData?.ud_id) {
        return Promise.reject("Missing required parameters");
      }
      try {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: true });
        const params = {
          ud_id: state.userData.ud_id,
          subcategory: "people-role",
          entity: "pro-group",
          subcategory_id: client_role_id,
          entity_id:
            state.userData.professional_group_role_user_data
              ?.professional_group_id,
        };
        await deleteRoleClientById(params);
        if (isMounted()) {
          const updatedclientHouseholds = state.clientHouseholds.filter(
            (p) => p.household_role?.id !== client_role_id
          );
          dispatch({ type: "SET_CLIENT_HOUSEHOLDS", payload: updatedclientHouseholds });
        }
      } catch (err) {
        toast.error("Failed to request access. Please try again.", {
          duration: 5000,
        });
        console.error(err);
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: false });
        }
      }
    },
    [
      isMounted,
      state.clientHouseholds,
      deleteRoleClientById,
      state.userData?.ud_id,
      state.userData?.professional_group_role_user_data?.professional_group_id,
    ]
  );

  const handleGetClientHouseholds = useCallback(
    async (
      userData: UserDataObj,
      searchAndPaginationState: ClientHouseholdsSearchState,
      signal?: AbortSignal
    ) => {
      if (!userData.ud_id || !userData.professional_group_role_user_data?.professional_group_id || !userData.full_name) {
        return;
      }

      const params = {
        ud_id: userData.ud_id,
        entity: "pro-group",
        entity_id: userData.professional_group_role_user_data?.professional_group_id,
        subcategory: "households",
      };
      const queryParams = {
        page: searchAndPaginationState.page + 1, // assuming 1-based pagination on the backend
        page_size: searchAndPaginationState.rowsPerPage,
        search: searchAndPaginationState.filter,
        ordering: `${searchAndPaginationState.sortDir === "desc" ? "-" : ""}${searchAndPaginationState.sortBy
          }`,
        role: "FINANCIAL_PROFESSIONAL_CLIENT",
        expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,head_of_household.total_payment_tier,household_role.pricing,transactions"
      };
      try {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_LOADING", payload: true });
        const response = await genericGetHouseholds(params, queryParams, signal);
        if (isMounted()) {
          // If a household exists where the head of household matches the userData's ud_id,
          // put it to the front of the list, removing the original one that is being replaced,
          // and set is_demo_client to true on its household_role.
          const households = response.data;
          const headOfHouseholdHouseholdIndex = households.findIndex(
            (household) => household.head_of_household?.ud_id === userData.ud_id
          );
          let headOfHouseholdHousehold = null;
          if (headOfHouseholdHouseholdIndex !== -1) {
            headOfHouseholdHousehold = households[headOfHouseholdHouseholdIndex];
            // Remove from its current position
            households.splice(headOfHouseholdHouseholdIndex, 1);
            // Add to the front
            households.unshift(headOfHouseholdHousehold);
          }
          dispatch({
            type: "SET_CLIENT_HOUSEHOLDS",
            payload: households,
          });
          dispatch({
            type: "SET_CLIENT_HOUSEHOLDS_SEARCH_COUNT",
            payload: response.count,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_LOADING", payload: false });
      }
    },
    [
      isMounted,
      genericGetHouseholds,
    ]
  );

  const handleGetClientCount = useCallback(
    async (userData: UserDataObj, signal?: AbortSignal) => {
      if (!userData.ud_id || !userData.professional_group_role_user_data?.professional_group_id) {
        return;
      }

      dispatch({ type: "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT_LOADING", payload: true });

      const params = {
        ud_id: userData.ud_id,
        entity: "pro-group",
        entity_id: userData.professional_group_role_user_data?.professional_group_id,
        subcategory: "households",
      };
      try {
        const response = await getClientCount(params, signal);
        if (isMounted()) {
          dispatch({
            type: "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT",
            payload: response.count,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted()) {
          dispatch({
            type: "SET_CLIENT_HOUSEHOLDS_TOTAL_COUNT_LOADING",
            payload: false,
          });
        }
      }
    },
    [getClientCount, isMounted]
  );

  const handleAdminUpdateClient = useCallback(
    async (
      client_role_id: string,
      client_role_updates: Partial<PeopleRole>
    ): Promise<Household> => {
      if (!client_role_id || !state.userData?.ud_id) {
        console.error("No client role id provided");
        return Promise.reject("No client role id provided");
      }

      try {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: true });
        const params = {
          ud_id: state.userData.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id || "",
          subcategory: "people-role",
          subcategory_id: client_role_id,
        };

        // Send all client_role_updates to the backend
        // Backend will handle which fields to update
        const data: any = { ...client_role_updates };

        const queryParams = {
          expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,household_role.pricing"
        };

        const res = await adminPathClientRole(params, data, true, queryParams);
        // response is entire refreshed household

        dispatch({ type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS", payload: res });
        return res;
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: false });
      }
    },
    [adminPathClientRole, state.userData?.ud_id, state.professionalGroup?.id]
  );

  const handleAddClient = useCallback(
    async (data: any): Promise<Household> => {
      if (!state.userData?.ud_id || !data.client_data.contact_email) {
        console.error("No user data id provided");
        return Promise.reject("No user data id provided");
      }

      // if (!data.spouse_data.contact_email) {
      //   // pop the spouse data if there's no email
      //   delete data.spouse_data;
      // }

      try {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: true });
        const params = {
          ud_id: state.userData.ud_id,
          entity_id: state.professionalGroup?.id || "",
          entity: "pro-group",
          subcategory: "households",
        };
        const queryParams = {
          expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,household_role.pricing"
        };
        const res = await addClient(params, data, queryParams);
        dispatch({ type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS", payload: res });
        return res;
      } catch (err) {
        let errorMessage = "Error adding client - please try again";
        
        if (err.response?.status === 400) {
          // Extract error message from various possible response formats
          errorMessage = err.response?.data?.details || 
                         err.response?.data?.detail || 
                         err.response?.data?.message ||
                         "Error adding client - check if client information is valid";
        } else if (err.response?.status === 403) {
          errorMessage = "You do not have permission to add this client";
        } else {
          errorMessage = `Failed to add client! Please try again or reach out to us at ${email_sender}`;
        }
        
        toast.error(errorMessage);
        console.error(err);
        throw err;
      } finally {
        dispatch({ type: "SET_CLIENT_HOUSEHOLDS_SAVING_NEW", payload: false });
      }
    },
    [
      addClient,
      state.userData?.ud_id,
      state.professionalGroup?.id,
      email_sender,
    ]
  );

  ////////////////////////////// PROFESSIONAL GROUP //////////////////////////////
  ////////////////////////////// PROFESSIONAL GROUP //////////////////////////////

  const [professionalGroupSaving, setProfessionalGroupSaving] = useState(false);
  const [professionalGroupLoading, setProfessionalGroupLoading] =
    useState(true);
  const getProfessionalGroup = useV3GenericGetData<ProfessionalGroup>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const updateProfessionalGroup = useV3Update<ProfessionalGroup>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const updateProfessionalGroupImage =
    useUploadProfessionalGroupImage<ProfessionalGroup>(
      GenericPath.PRO_GROUP_IMAGE
    );

  const removeProfessionalGroupImage = useV3DeleteById(
    GenericPath.PRO_GROUP_IMAGE
  );

  const handleUpdateProfessionalGroup = useCallback(
    async (data: ProfessionalGroup) => {
      if (!state.userData?.ud_id) return;

      try {
        setProfessionalGroupSaving(true);
        const params = {
          ud_id: state.userData.ud_id,
          entity: "pro-group",
          entity_id: data?.id || "",
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await updateProfessionalGroup(params, data, false, expand_params);
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      } catch (error) {
        console.error(error);
        toast.error(
          "There was an error updating your professional profile!"
        );
      } finally {
        setProfessionalGroupSaving(false);
      }
    },
    [updateProfessionalGroup, state.userData?.ud_id]
  );

  const handlePatchProfessionalGroup = useCallback(
    async (professionalGroupId: string, data: Partial<ProfessionalGroup>) => {
      if (!state.userData?.ud_id) return;

      try {
        setProfessionalGroupSaving(true);
        const params = {
          ud_id: state.userData.ud_id,
          entity: "pro-group",
          entity_id: professionalGroupId,
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await updateProfessionalGroup(params, data, true, expand_params);
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      } catch (error) {
        console.error(error);
        toast.error(
          "There was an error updating your professional profile!"
        );
      } finally {
        setProfessionalGroupSaving(false);
      }
    },
    [updateProfessionalGroup, state.userData?.ud_id]
  );

  const handleUpdateProfessionalGroupImage = useCallback(
    async (
      file: File,
      sourceFile: File,
      professionalGroup: ProfessionalGroup | null
    ) => {
      try {
        setProfessionalGroupSaving(true);
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await updateProfessionalGroupImage(
          state.userData?.ud_id || "",
          professionalGroup?.id || "",
          file,
          sourceFile,
          expand_params
        );
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        setProfessionalGroupSaving(false);
      }
    },
    [updateProfessionalGroupImage, state.userData?.ud_id]
  );

  const handleDeleteProGroupImage = useCallback(async () => {
    try {
      setProfessionalGroupSaving(true);
      const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
      const res = await removeProfessionalGroupImage("_id", expand_params);
      dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      toast.success("Your image has been deleted!");
    } catch (error) {
      console.error(error);
      toast.error(
        "There's an error deleting your professional professionalGroup image! Please try again."
      );
    } finally {
      setProfessionalGroupSaving(false);
    }
  }, [removeProfessionalGroupImage]);

  const handleGetProfessionalGroup = useCallback(
    async (proGroupId?: string, signal?: AbortSignal) => {
      try {
        const params = {
          entity: "pro-group",
          entity_id: proGroupId,
          ud_id: state.userData?.ud_id || "",
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await getProfessionalGroup(params, expand_params, signal);
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      } catch (error) {
        console.error(error);
      } finally {
        setProfessionalGroupLoading(false);
      }
    },
    [getProfessionalGroup, state.userData?.ud_id]
  );

  const handleInvitePro = useCallback(
    async (
      professional_group_id: string,
      name: string,
      email: string,
      role: "PRO_GROUP_ADMIN" | "PRO_GROUP_AGENT",
    ) => {
      if (!state.userData?.ud_id) {
        return;
      }

      try {
        // Step 1: Create the team member
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: professional_group_id,
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await invitePro(params, {
          name,
          email,
          role,
        }, expand_params);
        
        // Update the professional group state
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });

        // Step 2: Find the newly created role and send the invitation
        const newRole = res.professional_group_role_pro_group?.find(
          (r: any) => r.user_data?.contact_email === email.toLowerCase()
        );
        
        if (newRole) {
          const inviteParams = {
            ud_id: state.userData?.ud_id,
            entity: "pro-group",
            entity_id: professional_group_id,
            subcategory: "role",
            subcategory_id: newRole.id,
          };
          await sendProRoleInvite(inviteParams, {});
        }
        
      } catch (error) {
        console.error(error);
        toast.error("Failed to invite professional!");
      }
    },
    [state.userData?.ud_id, invitePro, sendProRoleInvite]
  );

  const handleSendInvitation = useCallback(
    async (role_id: string) => {
      if (!state.userData?.ud_id || !state.professionalGroup?.id) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup.id,
          subcategory: "role",
          subcategory_id: role_id,
        };
        await sendProRoleInvite(params, {});
        toast.success("Invitation sent successfully!");
      } catch (error) {
        console.error(error);
        toast.error("Failed to send invitation!");
      }
    },
    [state.userData?.ud_id, state.professionalGroup?.id, sendProRoleInvite]
  );

  const handleInviteClient = useCallback(
    async (
      userDataUdId: string,
      customMessage: string | null,
      clientNotification: boolean = false
    ) => {
      if (
        !state.userData?.ud_id ||
        !state.professionalGroup?.id ||
        !userDataUdId
      ) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id,
          subcategory: "user-data",
          subcategory_id: userDataUdId,
        };

        await sendClientInvite(params, {
          custom_message: customMessage,
        });

        if (clientNotification) {
          toast.success("Invitation sent!");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to send invitation!");
      }
    },
    [state.userData?.ud_id, state.professionalGroup?.id, sendClientInvite]
  );

  const handleTransferClient = useCallback(
    async (
      client_role_id: string,
      transfer_to_professional_role_id: string,
      transfer_from_professional_role_id: string
    ) => {
      if (
        !state.userData?.ud_id ||
        !state.professionalGroup?.id ||
        !client_role_id
      ) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id,
          subcategory: "pro-people-roles",
          subcategory_id: client_role_id,
        };

        const res = await transferClient(
          params,
          {
            transfer_to_professional_role_id: transfer_to_professional_role_id,
            transfer_from_professional_role_id:
              transfer_from_professional_role_id,
          },
          true
        );

        // response is updated client role. update in state
        const updatedHouseholds = state.clientHouseholds.find((household) => household.household_role?.id === client_role_id);
        if (updatedHouseholds) {
          updatedHouseholds.household_role = res;
        }
        if (updatedHouseholds) {
          dispatch({ type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS", payload: updatedHouseholds });
        }
        toast.success("Client transferred!");
      } catch (error) {
        console.error(error);
        toast.error("Failed to transfer client!");
      }
    },
    [state.userData?.ud_id, transferClient, state.professionalGroup?.id, state.clientHouseholds]
  );

  const handleUpgradeToAdmin = useCallback(
    async (pro_role_id: string) => {
      if (
        !state.userData?.ud_id ||
        !state.professionalGroup?.id ||
        !pro_role_id
      ) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id,
          subcategory: "role",
          subcategory_id: pro_role_id,
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await upgradeToAdmin(params, {}, expand_params);

        // response is updated professional group. set it to state
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      } catch (error) {
        console.error(error);
        toast.error("Failed to update team member role");
      }
    },
    [state.userData?.ud_id, upgradeToAdmin, state.professionalGroup?.id]
  );

  const handleDowngradeToAgent = useCallback(
    async (pro_role_id: string) => {
      if (
        !state.userData?.ud_id ||
        !state.professionalGroup?.id ||
        !pro_role_id
      ) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id,
          subcategory: "role",
          subcategory_id: pro_role_id,
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await downgradeToAgent(params, {}, expand_params);


        // response is updated professional group. set it to state
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
      } catch (error) {
        console.error(error);
        toast.error("Failed to update team member role");
      }
    },
    [state.userData?.ud_id, downgradeToAgent, state.professionalGroup?.id]
  );

  const handleDeleteProGroupRole = useCallback(
    async (pro_role_id: string, pro_group_role_id: string) => {
      if (
        !state.userData?.ud_id ||
        !state.professionalGroup?.id ||
        !pro_role_id
      ) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id,
          subcategory: "role",
          subcategory_id: pro_role_id,
        };
        const queryParams = {
          pro_group_role_id,
        };
        await deleteProGroupRole(params, queryParams);

        // response is empty, remove the role from the list in the pro group state object: professional_group_role_pro_group by id
        const updatedRoles =
          state.professionalGroup?.professional_group_role_pro_group?.filter(
            (role) => role.id !== pro_role_id
          );
        if (state.professionalGroup) {
          const updatedProfessionalGroup = {
            ...state.professionalGroup,
            professional_group_role_pro_group: updatedRoles || [],
          };
          dispatch({
            type: "SET_PROFESSIONAL_GROUP",
            payload: updatedProfessionalGroup,
          });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete client role!");
      }
    },
    [state.userData?.ud_id, deleteProGroupRole, state.professionalGroup]
  );

  const handleDeletePeopleRole = useCallback(
    async (ud_id: string, people_role_id: string) => {
      if (!ud_id || !people_role_id) {
        return;
      }

      try {
        const params = {
          ud_id: ud_id,
          entity: "people-roles",
          entity_id: people_role_id,
        };
        await deletePeopleRole(params);
        toast.success("Spouse removed successfully");
      } catch (error) {
        console.error(error);
        toast.error("Failed to remove spouse!");
      }
    },
    [deletePeopleRole]
  );

  const handleEditClientEmail = useCallback(
    async (client_role_id: string, email: string, send_invite: boolean) => {
      if (!state.userData?.ud_id || !client_role_id) {
        return;
      }

      try {
        const params = {
          ud_id: state.userData?.ud_id,
          entity: "pro-group",
          entity_id: state.professionalGroup?.id || "",
          subcategory: "pro-people-roles",
          subcategory_id: client_role_id,
        };

        const res = await editClientEmail(params, { email, send_invite }, true);

        // response is updated client role. update in state
        const updatedHouseholds = state.clientHouseholds.find((household) => household.household_role?.id === client_role_id);
        if (updatedHouseholds) {
          updatedHouseholds.household_role = res;
        }
        if (updatedHouseholds) {
          dispatch({ type: "ADD_OR_UPDATE_CLIENT_HOUSEHOLDS", payload: updatedHouseholds });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to update client email!");
      }
    },
    [state.userData?.ud_id, editClientEmail, state.professionalGroup?.id, state.clientHouseholds]
  );


  const handleRefreshProGroup =
    useCallback(async (): Promise<ProfessionalGroup | null> => {
      if (
        !state.userData?.ud_id ||
        !state.userData?.professional_group_role_user_data
          ?.professional_group_id
      ) {
        return null;
      }
      setProfessionalGroupSaving(true);
      try {
        const params = {
          entity: "pro-group",
          entity_id:
            state.userData?.professional_group_role_user_data
              .professional_group_id,
          ud_id: state.userData?.ud_id || "",
        };
        const expand_params = { expand: "white_label_config,professional_group_role_pro_group,professional_group_role_pro_group.user_data" };
        const res = await getProfessionalGroup(params, expand_params);
        dispatch({ type: "SET_PROFESSIONAL_GROUP", payload: res });
        return res;
      } catch (error) {
        console.error(error);
        return null;
      } finally {
        setProfessionalGroupSaving(false);
      }
    },
      [
        getProfessionalGroup,
        state.userData?.ud_id,
        state.userData?.professional_group_role_user_data?.professional_group_id,
      ]);

  ////////////////////////////// PROFESSIONAL GROUP //////////////////////////////
  ////////////////////////////// PROFESSIONAL GROUP //////////////////////////////

  // get owner's user datga. If it doesn't exist, create it.
  useEffect(() => {
    const abortController = new AbortController();

    const handleGetUserData = async (authUser: User, signal?: AbortSignal): Promise<void> => {
      try {
        const params = { ud_id: "", entity: "user_data" };
        const expand_params = { expand: "professional_group_role.professional" };
        const response = await genericGetUserData(params, expand_params, signal);
        dispatch({ type: "SET_USERDATA", payload: response });

      } catch (err) {
        if (abortController.signal.aborted) return;
        // if there's an error, create the user data object!
        try {
          const res = await handleAddOrEditOwnerUserData({
            ud_id: "",
            full_name: authUser?.first_name,
            contact_email: authUser?.username,
            assigned_total_domain: "app.getsnug.com",
          });
          if (!res) {
            throw new Error("User data not created");
          }
          dispatch({ type: "SET_USERDATA", payload: res });
        } catch (error) {
          const searchParams = new URLSearchParams({
            returnTo: window.location.pathname + window.location.search,
          }).toString();
          const href = paths.auth.jwt.entryStart + `?${searchParams}`;
          router.replace(href);
        }
      } finally {
        if (!abortController.signal.aborted) {
          dispatch({ type: "SET_USERDATA_INITIAL_LOADING", payload: false });
        }
      }
    };

    if (user) {
      handleGetUserData(user, abortController.signal);
    }

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, router]);

  // get client roles
  useEffect(() => {
    // abort signal
    const abortController = new AbortController();
    if (state.userData) {
      handleGetClientHouseholds(
        state.userData,
        searchAndPaginationState,
        abortController.signal
      );
    }

    return () => {
      abortController.abort();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.userData, searchAndPaginationState]
  );

  // get people roles data.
  useEffect(() => {
    const abortController = new AbortController();
    if (
      state.userData
    ) {
      handleGetClientCount(
        state.userData,
        abortController.signal
      );
    }

    return () => {
      abortController.abort();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.userData
    ]);

  // get professional group data
  useEffect(() => {
    // abort signal
    const abortController = new AbortController();
    if (state.userData?.professional_group_role_user_data) {
      handleGetProfessionalGroup(
        state.userData?.professional_group_role_user_data.professional_group_id,
        abortController.signal
      );
    }

    return () => {
      abortController.abort();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.userData?.professional_group_role_user_data
    ]);

  useEffect(() => {
    if (state.professionalGroup) {
      // Extract user data for tracking
      const userData = state.userData ? {
        email: state.userData.contact_email || undefined,
        firstName: state.userData.full_name?.split(' ')[0] || undefined,
        lastName: state.userData.full_name?.split(' ').slice(1).join(' ') || undefined,
        ud_id: state.userData.ud_id || undefined,
      } : undefined;

      gtm.pushWithBackend({
        event: "professionalGroupDataChange",
        professionalGroup: state.professionalGroup,
      }, userData);

      // if the user just logged in, push a login event - this is registered in the auth context. This is marketing engineering so WAHHHHHH
      if (sessionStorage.getItem("login")) {
        gtm.pushWithBackend({
          event: "login",
          professionalGroup: state.professionalGroup,
        }, userData);
        sessionStorage.removeItem("login");
      }
    }
  }, [state.professionalGroup, state.userData]);

  const handleRefreshHousehold = useCallback(async (householdId: string ) => {
    if (!state.userData?.ud_id) {
      return;
    }
    // this function refreshes a single household then updates the household in the state array of households based on id
    const params = { ud_id: state.userData?.ud_id , entity: "household", entity_id: householdId };
    const expand_params = { expand: "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,head_of_household.estate_documents,head_of_household.household,household_role.pricing,head_of_household.total_payment_tier," };
    const response = await genericGetHousehold(params, expand_params);
    // update the household in the state array of households based on id
    const updatedHouseholds = state.clientHouseholds.map((household) => {
      if (household.household_role?.id === householdId) {
        return response;
      }
      return household;
    });
    // Dispatch the updated households to actually update the state
    dispatch({ type: "SET_CLIENT_HOUSEHOLDS", payload: updatedHouseholds });
  }, [state.userData?.ud_id, state.clientHouseholds, genericGetHousehold]);

  const handleAddProGroup = useV3Create<ProfessionalGroup>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const [creatingProGroup, setCreatingProGroup] = useState(false);

  useEffect(() => {

    const handleGetUserData = async (signal?: AbortSignal): Promise<void> => {
      try {
        const params = { ud_id: "", entity: "user_data" };
        const expand_params = { expand: "professional_group_role.professional" };
        const response = await genericGetUserData(params, expand_params, signal);
        dispatch({ type: "SET_USERDATA", payload: response });

      } catch (err) {
        console.error(err);
      }
    };

    const handleCreateProfessionalGroup = async (userData: UserDataObj) => {
      setCreatingProGroup(true);
      const params = {
        entity: "pro-group",
        ud_id: userData.ud_id,
      };
      try {

        await handleAddProGroup(params, {
          name: userData.full_name + "'s Firm",
          professional_classification: null,
          org_size: null,
          referred_by_user_ud_id: null,
        });

        // refresh the user data
        await handleGetUserData();
      } catch (error) {
        console.error(error);
      } finally {
        setCreatingProGroup(false);
      }
    }


    if (
      state.userData &&
      !state.userData?.professional_group_role_user_data
    ) {
      handleCreateProfessionalGroup(state.userData);
    }
  }, [state.professionalGroup, state.userData, router, genericGetUserData, handleAddProGroup]);

  if (state.professionalGroup?.dashboard_wl_on &&
    dashboard_domain !== state.professionalGroup.white_label_config?.dashboard_domain
  ) {
    signOut();
    const href = paths.auth.jwt.entryStart + `?ep=login&lb=true`;
    router.replace(href);
    return <SplashScreen />;
  }

  if (!state.userData || professionalGroupLoading || creatingProGroup) {
    return <SplashScreen />;
  }

  const isPaidTier =
    state.professionalGroup?.onboarding_payment_date !== null;
  const isFirmAdmin =
    state.userData.professional_group_role_user_data?.role ===
    "PRO_GROUP_ADMIN";

  const handleUpdateWhiteLabelConfig = async (data: {
    email_sender_name?: string;
    product_name?: string;
    disclaimer?: string;
    domain_provider?: string;
    domain?: string;
    dashboard_domain?: string;
    email_sender?: string;
  }): Promise<void> => {
    if (!state.professionalGroup?.id || !state.userData?.ud_id) {
      throw new Error("No professional group found");
    }

    try {
      dispatch({ type: "SET_WHITELABEL_CONFIG_SAVING", payload: true });

      const params = {
        ud_id: state.userData?.ud_id,
        entity: "pro-group",
        entity_id: state.professionalGroup.id,
      }

      await handleUpdateWhiteLabelConfigOnProGroup(params, data, true);

      // Refresh the professional group to get updated white label config
      await handleRefreshProGroup();

    } catch (err) {
      console.error(err);
      toast.error("Failed to update");
      throw err;
    } finally {
      dispatch({ type: "SET_WHITELABEL_CONFIG_SAVING", payload: false });
    }
  };

  const value = {
    userData: state.userData,
    userDataInitialLoading: state.userDataInitialLoading,
    userDataSaving: state.userDataSaving,
    clientHouseholds: state.clientHouseholds,
    clientHouseholdsSearchCount: state.clientHouseholdsSearchCount,
    clientHouseholdsTotalCount: state.clientHouseholdsTotalCount,
    clientHouseholdsTotalCountLoading: state.clientHouseholdsTotalCountLoading,
    clientHouseholdsLoading: state.clientHouseholdsLoading,
    clientHouseholdsSavingNew: state.clientHouseholdsSavingNew,
    clientHouseholdsFilestoUpload: state.clientHouseholdsFilestoUpload,
    professionalGroup: state.professionalGroup,
    professionalGroupSaving: professionalGroupSaving,
    whitelabelConfigSaving: state.whitelabelConfigSaving,
    searchAndPaginationState,
    isFirmAdmin,
    isPaidTier,
    handleFiltersChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleUpdateProfessionalGroup,
    handleUpdateProfessionalGroupImage,
    handleDeleteProGroupImage,
    handleArchiveClientRole,
    handleDownloadClientEstateDocument,
    handleDownloadAllClientEstateDocuments,
    handleclientHouseholdsFileRemove,
    handleclientHouseholdsFilesDrop,
    handleclientHouseholdsFilesRemoveAll,
    handleUploadClientHouseholdsFiles,
    handleAddOrEditTargetUserData,
    handleUpdateWhiteLabelConfig,
    handleDeleteProfessionalImage,
    handleUpdateProfessionalImage,
    handleAddOrEditOwnerUserData,
    handleInvitePro,
    handleSendInvitation,
    handleInviteClient,
    handleUpgradeToAdmin,
    handleDowngradeToAgent,
    handleDeleteProGroupRole,
    handleDeletePeopleRole,
    handleAdminUpdateClient,
    handleTransferClient,
    handleRequestAccessFromLead,
    handleEditClientEmail,
    handleRefreshProGroup,
    handlePatchSelfProfessionalGroupRole,
    handleAddClient,
    updateProfessionalGroupState,
    handlePatchProfessionalGroup,
    updateHeadOfHouseholdUserData,
    handleRefreshHousehold,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

export default function useUserDataProvider() {
  return useContext(UserDataContext);
}
